if (!customElements.get('add-to-cart-mobile')) {
    class AddToCartMobile extends HTMLElement {
        static SELECTORS = {
            trigger: '[data-add-to-cart-trigger]',
            sizesContainer: '.sizes-container',
            sizeButtons: '[data-submit-mobile]',
            overlay: '.popup-overlay',
            productForm: 'form.shopify-product-form',
            variantIdInput: 'input[name="id"]',
            addedPopover: 'x-popover.product-added',
            addedPopoverBorder: '.product-added .animated-border',
            quickBuyModal: 'quick-buy-modal'
        };

        static CLASSES = {
            hiddenPhone: 'hidden-phone',
            outOfStock: 'block-swatch__item--out-of-stock',
            animationStarted: 'animation-started',
            modalSizesOnly: 'modal--sizes-only'
        };

        // Instances with this attribute (the mobile sticky bar) only open the sheet: the in-flow header owns the
        // size buttons, otherwise every instance would submit the same size tap.
        static ATTRIBUTES = { triggerOnly: 'trigger-only' };

        static EVENTS = {
            modalShown: 'dialog:after-show',
            variantAdd: 'variant:add',
            cartError: 'cart:error'
        };

        static MOBILE_MAX_WIDTH = 999;
        static CLOSE_DELAY_MS = 100;

        // Safety net for _closeAfterAdd: if the add never settles, the sheet still closes.
        static ADD_TIMEOUT_MS = 6000;

        // The quick-buy modal reparents itself to <body> on show and back on hide, so this
        // element disconnects/reconnects several times per open. Every connect rebuilds the
        // state from the current DOM; AbortController drops the previous listeners.
        connectedCallback() {
            this._abort = new AbortController();
            this._isMobile = window.innerWidth <= AddToCartMobile.MOBILE_MAX_WIDTH;
            this._quickBuyModal = this.closest(AddToCartMobile.SELECTORS.quickBuyModal);

            const scope = this._findScope();
            this._sizesContainer = scope.querySelector(AddToCartMobile.SELECTORS.sizesContainer);
            this._form = scope.querySelector(AddToCartMobile.SELECTORS.productForm);

            if (!this._sizesContainer) return;

            this._bindEvents();
        }

        disconnectedCallback() {
            this._abort?.abort();

            if (!this._quickBuyModal) return;

            // Only drop the sheet once it was actually moved out to <body> — the modal's
            // show/hide reparenting also disconnects us while the sheet is still inside it
            if (this._sizesContainer?.parentElement === document.body) {
                this._sizesContainer.remove();
            }

            // Un-hiding the modal here (not in close) avoids a flash during its fade-out,
            // and keeps it usable if the next open happens on a desktop viewport
            this._quickBuyModal.classList.remove(AddToCartMobile.CLASSES.modalSizesOnly);
        }

        open() {
            // Inside the quick-buy modal the sheet must escape the modal's stacking
            // context (z-index 999 + animated transform) to display fixed above it.
            // The modal keeps hosting the content but is hidden so only the sheet shows
            if (this._quickBuyModal) {
                this._quickBuyModal.classList.add(AddToCartMobile.CLASSES.modalSizesOnly);

                if (this._sizesContainer.parentElement !== document.body) {
                    document.body.append(this._sizesContainer);
                }
            }

            this._sizesContainer.classList.remove(AddToCartMobile.CLASSES.hiddenPhone);
        }

        close() {
            this._sizesContainer.classList.add(AddToCartMobile.CLASSES.hiddenPhone);
            this._hideAddedPopover();

            // The modal rebuilds its content on every show, so the moved-out sheet can be
            // dropped right away instead of waiting for the modal's after-hide teardown
            if (this._quickBuyModal) {
                if (this._sizesContainer.parentElement === document.body) this._sizesContainer.remove();
                this._quickBuyModal.hide();
            }
        }

        _bindEvents() {
            const { signal } = this._abort;

            this.querySelector(AddToCartMobile.SELECTORS.trigger)
                ?.addEventListener('click', () => this.open(), { signal });

            if (this.hasAttribute(AddToCartMobile.ATTRIBUTES.triggerOnly)) return;

            this._sizesContainer.querySelectorAll(AddToCartMobile.SELECTORS.sizeButtons).forEach((button) =>
                button.addEventListener('click', (event) => this._onSizeClick(event), { signal })
            );

            this._sizesContainer.querySelector(AddToCartMobile.SELECTORS.overlay)
                ?.addEventListener('click', () => this.close(), { signal });

            // Quick buy: skip the intermediate popup step and show the sizes right away.
            // Once the modal is done reparenting itself to <body> we can open immediately
            // (also covers re-renders after "dialog:after-show" already fired); before
            // that, opening would get wiped by the reparenting, so wait for after-show
            if (this._quickBuyModal && this._isMobile) {
                this._allowSheetInteractions();

                if (this._quickBuyModal.parentElement === document.body) {
                    this.open();
                } else {
                    this._quickBuyModal.addEventListener(
                        AddToCartMobile.EVENTS.modalShown,
                        () => this.open(),
                        { signal }
                    );
                }
            }
        }

        _onSizeClick(event) {
            event.preventDefault();

            if (!this._isMobile) return;

            const button = event.currentTarget;

            // Out-of-stock sizes open the back-in-stock popover via their own toggle-button
            if (button.classList.contains(AddToCartMobile.CLASSES.outOfStock)) return;

            const variantId = button.getAttribute('data-id');
            if (!variantId) return;

            this._syncFormVariant(variantId);

            if (!this._form) {
                addToCart({ id: variantId, quantity: 1 }, true, true, false, true);
                this._closeSoon();
                return;
            }

            // Submit through the theme's <product-form> rather than calling addToCart directly:
            // that fires the standard variant:add / cart:change chain, which the Shop the Look
            // drawer fills its panel from. Calling addToCart instead surfaces the theme's older
            // add-to-cart popover, which the new drawer replaces.
            this._closeAfterAdd();
            this._form.requestSubmit();
        }

        _closeSoon() {
            setTimeout(() => this.close(), AddToCartMobile.CLOSE_DELAY_MS);
        }

        // The theme dispatches variant:add on the form element itself, and only once two fetches
        // have resolved. Closing the modal first detaches that form, so the event bubbles nowhere
        // and the drawer opens with an empty panel while the theme's own toast goes unsuppressed —
        // two notifications, neither complete. So wait for the add to settle, then close.
        _closeAfterAdd() {
            let closed = false;
            const finish = () => {
                if (closed) return;
                closed = true;
                this._closeSoon();
            };

            // Deliberately not bound to this._abort: the modal reparents itself while the request
            // is in flight, which disconnects this element and would abort the very listeners we
            // are waiting on. `once` plus the guard keeps them self-cleaning.
            this._form.addEventListener(AddToCartMobile.EVENTS.variantAdd, finish, { once: true });
            this._form.addEventListener(AddToCartMobile.EVENTS.cartError, finish, { once: true });
            setTimeout(finish, AddToCartMobile.ADD_TIMEOUT_MS);
        }

        _syncFormVariant(variantId) {
            const input = this._form?.querySelector(AddToCartMobile.SELECTORS.variantIdInput);
            if (input) input.value = variantId;
        }

        _hideAddedPopover() {
            document.querySelector(AddToCartMobile.SELECTORS.addedPopover)?.hide();
            document.querySelector(AddToCartMobile.SELECTORS.addedPopoverBorder)
                ?.classList.remove(AddToCartMobile.CLASSES.animationStarted);
        }

        // The modal's focus trap treats any real tap on the sheet as an "outside click":
        // it blocks the event before our listeners run and closes the modal, so nothing is
        // added to the cart. The theme exposes these two hooks for exactly this — teach the
        // modal that the sheet (moved to <body>) belongs to it. Patched once per modal.
        _allowSheetInteractions() {
            const modal = this._quickBuyModal;

            if (modal._sheetInteractionsAllowed) return;
            modal._sheetInteractionsAllowed = true;

            const isSheetTarget = (target) => !!target?.closest?.(AddToCartMobile.SELECTORS.sizesContainer);
            const originalHideFor = modal.hideForOutsideClickTarget.bind(modal);
            const originalAllowFor = modal.allowOutsideClickForTarget.bind(modal);

            modal.hideForOutsideClickTarget = (target) => !isSheetTarget(target) && originalHideFor(target);
            modal.allowOutsideClickForTarget = (target) => isSheetTarget(target) || originalAllowFor(target);
        }

        // Quick-buy instances must never reach outside their modal: with duplicate
        // #sizes-container ids on product pages they would grab the main product's sheet
        _findScope() {
            if (this._quickBuyModal) return this._quickBuyModal;

            let node = this.parentElement;

            while (node && node !== document.body && !node.querySelector(AddToCartMobile.SELECTORS.sizesContainer)) {
                node = node.parentElement;
            }

            return node || document;
        }
    }

    customElements.define('add-to-cart-mobile', AddToCartMobile);
}
